import {
  Component, OnInit, ExistingProvider, forwardRef, HostListener, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef,
  SimpleChanges, ViewChildren, QueryList,
} from '@angular/core';
import {
  UntypedFormGroup, UntypedFormBuilder, NG_VALUE_ACCESSOR, UntypedFormArray, FormControl, FormGroup, AbstractControl,
} from '@angular/forms';
import {
  noop, isNil, size, get, isEqual,
} from 'lodash';
import { IMedia, IMediaItem } from 'typings/section-types';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { UploadableDirective } from 'src/directives/uploadable.directive';
import { IUploadResponse } from 'typings/custom-app-types';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { AssetService } from 'src/api/media/asset/asset.service';
import { MediaOriginTypeQuery } from 'src/api/generic/media-origin-type/media-origin-type.query';
import { IMedia as ICompleteMedia, IMediaOriginType } from 'typings/doenkids/doenkids';
import { MatDialog } from '@angular/material/dialog';
import { IReplaceImageDialogData, ReplaceImageDialogComponentComponent } from 'src/components/dialogs/replace-image-dialog-component/replace-image-dialog-component.component';
import { DoenkidsImageComponent } from 'src/components/layout/doenkids-image/doenkids-image.component';

export const SECTION_IMAGE_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => ImageSectionComponent),
  multi: true,
};

@Component({
    selector: 'app-image-section',
    templateUrl: './image-section.component.html',
    styleUrls: ['./image-section.component.scss'],
    providers: [SECTION_IMAGE_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class ImageSectionComponent extends UploadableDirective implements OnInit, OnDestroy {
  public stop$ = new Subject<void>();

  @Input()
  public purpose?: string;

  @Input()
  public identifier: string;

  @Input()
  public maxMediaItems?: number | null;

  @Output()
  public changedSection = new EventEmitter<void>();

  public form: UntypedFormGroup;

  /** Value we will call whenever our form is touched */
  private onTouchedCallback: Function = noop;

  /** Function we will call whenever the value changes */
  private onChangeCallback: (val: IMedia) => void = noop;

  public get mediaFormArray(): UntypedFormArray {
    return this.form.get('media') as UntypedFormArray;
  }

  @HostListener('blur') onBlur() {
    this.onTouchedCallback();
  }

  public isAdmin$: Observable<boolean>;

  public originTypeOptions$: Observable<IMediaOriginType[]>;
  private uploadSubscriptionInitialized = false;

  /** The images, one per media control and in the same order */
  @ViewChildren(DoenkidsImageComponent) images: QueryList<DoenkidsImageComponent>;

  /** The source pickers with a save running; disabled meanwhile, so only one save per image is in flight */
  public savingOriginTypes = new Set<AbstractControl>();

  constructor(
    private fb: UntypedFormBuilder,
    private $asset: AssetService,
    private $mediaOriginType: MediaOriginTypeQuery,
    private matDialog: MatDialog,
    $session: DoenkidsSessionProvider,
    private cd: ChangeDetectorRef,
  ) {
    super();
    this.form = this.fb.group({
      title: [''],
      subtitle: [''],
      media: this.fb.array([]),
    });
    this.isAdmin$ = $session.isAdmin$;
    this.originTypeOptions$ = this.$mediaOriginType.selectOptions();
  }

  ngOnInit() {
    this.form.valueChanges.subscribe((formValue) => {
      this.onChangeCallback(formValue);
    });
    this.setupUploadSubscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.uploadData) {
      this.setupUploadSubscription();
    }
  }

  ngOnDestroy(): void {
    this.stop$.next();
  }

  addImageItem(imageItem: IUploadResponse) {
    const mediaFormArray = this.form.get('media') as UntypedFormArray;
    const normalizedImageItem = {
      ...imageItem,
      uuid: get(imageItem, 'uuid', get(imageItem, 'data.uuid', get(imageItem, 'item.uuid', ''))),
    } as IUploadResponse;

    if (this.maxMediaItems === 1 && mediaFormArray.length >= 1) {
      const firstGroup = mediaFormArray.at(0) as FormGroup;
      firstGroup.patchValue({
        description: get(normalizedImageItem, 'description', ''),
        caption: get(normalizedImageItem, 'caption', ''),
        uuid: get(normalizedImageItem, 'uuid', ''),
      });
      this.addOriginTypeControl(firstGroup, true);
      return;
    }

    if (this.maxMediaItems && mediaFormArray.length >= this.maxMediaItems) {
      return;
    }

    const newGroup = this.createMediaGroup(normalizedImageItem);

    this.addOriginTypeControl(newGroup);

    mediaFormArray.push(newGroup);
  }

  private setupUploadSubscription() {
    if (this.uploadSubscriptionInitialized || !this.uploadData?.uploaded) {
      return;
    }

    this.uploadSubscriptionInitialized = true;
    this.uploadData.uploaded.pipe(
      filter((imageItem) => !isNil(imageItem)),
      takeUntil(this.stop$),
    ).subscribe((imageItem) => {
      this.addImageItem(imageItem);
    });
  }

  async writeValue(value: IMedia) {
    if (value) {
      this.form.get('title').setValue(value.title || '');
      this.form.get('subtitle').setValue(value.subtitle || '');

      const mediaFormArray = this.form.get('media') as UntypedFormArray;
      const sourceMedia = value.media ?? [];
      const mediaItems = this.maxMediaItems ? sourceMedia.slice(0, this.maxMediaItems) : sourceMedia;

      while (mediaFormArray.length > mediaItems.length) {
        mediaFormArray.removeAt(mediaFormArray.length - 1);
      }

      for (let index = 0; index < mediaItems.length; index += 1) {
        const media = mediaItems[index];
        let control = mediaFormArray.at(index) as FormGroup;
        if (isNil(control) && size(media.uuid)) {
          control = this.createMediaGroup(media);

          await this.addOriginTypeControl(control);

          mediaFormArray.insert(index, control);
        } else if (control) {
          const previousUuid = control.get('uuid')?.value;
          control.patchValue({
            description: get(media, 'description', ''),
            caption: get(media, 'caption', ''),
            uuid: get(media, 'uuid', ''),
          });
          const newUuid = get(media, 'uuid', '');
          if (previousUuid !== newUuid) {
            this.addOriginTypeControl(control, true);
          }
        }
      }
    }
  }

  removeMedia(index) {
    this.mediaFormArray.removeAt(index);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  async addOriginTypeControl(group: FormGroup, refreshExisting: boolean = false) {
    const isAdmin = await firstValueFrom(this.isAdmin$);
    const uuid = group.get('uuid')?.value;

    if (isAdmin && uuid) {
      const originTypeControlExists = !!group.get('originType');
      const mediaControlExists = !!group.get('media');

      if (!refreshExisting && originTypeControlExists && mediaControlExists) {
        return;
      }

      const media = await this.$asset.fetchByUuid(uuid);
      if (!media) {
        return;
      }
      const mediaType = this.$mediaOriginType.getTypeForId(media.media_origin_type_id);

      if (!mediaControlExists) {
        const mediaControl = new FormControl(media);
        group.addControl('media', mediaControl);
      } else {
        const mediaControl = group.get('media');
        mediaControl?.setValue(media, { emitEvent: false });
      }

      if (!originTypeControlExists) {
        const typeControl =  new FormControl(mediaType?.id ?? 'unknown');
        group.addControl('originType', typeControl);
        this.setupOriginTypeListener(typeControl, group);
      } else {
        const typeControl = group.get('originType') as FormControl;
        typeControl?.setValue(mediaType?.id ?? 'unknown', { emitEvent: false });
      }
    }
  }

  setupOriginTypeListener(control: FormControl, group: FormGroup) {
    control.valueChanges.pipe(takeUntil(this.stop$), distinctUntilChanged(isEqual)).subscribe((originType) => {
      const mediaItemControl = group.get('media');
      const mediaItem = mediaItemControl?.value as ICompleteMedia;

      if (mediaItem) {
        let originTypeId = null;
        if (originType !== 'unknown') {
          const mediaOriginType = this.$mediaOriginType.getTypeForId(originType);

          if (mediaOriginType) {
            originTypeId = mediaOriginType.id;
          }
        }

        if (originTypeId !== mediaItem.media_origin_type_id) {
          this.saveOriginType(control, group, originTypeId);
        }
      }
    });
  }

  /**
   * Saves one image's origin type. Media-api burns the AI label into the image
   * files and gives a relabelled image new file names, so the image is reloaded
   * with a new `t` to show the label. A failed save (already toasted) puts the
   * picker back.
   */
  private async saveOriginType(control: FormControl, group: FormGroup, originTypeId: number | null) {
    const mediaItemControl = group.get('media');

    this.savingOriginTypes.add(control);
    control.disable({ emitEvent: false });

    const newMedia = { ...mediaItemControl.value, media_origin_type_id: originTypeId } as ICompleteMedia;
    const saved = await firstValueFrom(this.$asset.update(newMedia), { defaultValue: null }).catch(() => null);

    // Unless the whole section was disabled meanwhile: enabling one control would enable the form again.
    if (!this.form.disabled) {
      control.enable({ emitEvent: false });
    }
    this.savingOriginTypes.delete(control);

    // The image was changed meanwhile, so the result belongs to the previous one.
    if ((mediaItemControl.value as ICompleteMedia)?.id !== newMedia.id) {
      return;
    }

    if (saved) {
      mediaItemControl.setValue(saved, { emitEvent: false });
      this.images.get(this.mediaFormArray.controls.indexOf(group))?.reloadImage();
    } else {
      control.setValue((mediaItemControl.value as ICompleteMedia).media_origin_type_id ?? 'unknown');
    }
  }

  async replaceImage(mediaControl: AbstractControl) {
    const dialogRef = this.matDialog.open(ReplaceImageDialogComponentComponent, {
      maxHeight: '95vh',
      maxWidth: '600px',
      panelClass: 'image-replace-modal',
      data: {
        currentImageUuid:( mediaControl.value as IMediaItem)?.uuid,
        purpose: this.purpose,
        identifier: this.identifier,
      } as IReplaceImageDialogData,
    });

    const result: ICompleteMedia = await firstValueFrom(dialogRef.afterClosed());

    if (result) {
      mediaControl?.get('uuid')?.setValue(result.uuid);
      mediaControl.markAsDirty();
      this.changedSection.emit();
      this.cd.detectChanges();
    }
  }

  private createMediaGroup(media: Partial<IMediaItem> | Partial<IUploadResponse> = {}) {
    return this.fb.group({
      description: [get(media, 'description', '')],
      caption: [get(media, 'caption', '')],
      uuid: [get(media, 'uuid', '')],
    });
  }
}
